import { useCallback, useEffect, useState } from 'react';
import {
  isNotificationApiSupported,
  readNotificationPermission,
  requestNotificationPermission,
  spawnNotification,
  type NotificationPermissionState,
} from '@/lib/notifications/dom';

export interface NotifyArgs {
  title: string;
  body: string;
  /**
   * Used as the OS-level `tag` so successive arrivals on the same thread
   * collapse into one notification instead of stacking.
   */
  threadId: string;
  /** Optional click handler. Called after `window.focus()` succeeds. */
  onClick?: () => void;
}

export interface UseBrowserNotificationsResult {
  /** Current permission, or `'unsupported'` on platforms without the API. */
  permission: NotificationPermissionState;
  /** Whether the Notification API is even available. */
  isSupported: boolean;
  /** Convenience: `permission === 'default'` AND supported. */
  canRequest: boolean;
  /** Triggers the native permission dialog. No-op outside `'default'`. */
  requestPermission: () => Promise<NotificationPermissionState>;
  /**
   * Surface a notification when permission is granted. Fires regardless
   * of tab visibility — modern OS chrome handles a focused tab gracefully
   * (most show a brief banner, some suppress it; either is fine). The
   * earlier "only when hidden" guard made notifications invisible during
   * active testing and surprised the user, so it was removed.
   * Returns true if a notification was actually spawned.
   */
  notify: (args: NotifyArgs) => boolean;
}

/**
 * Browser-notification glue. Stateless on its own — the inbox page decides
 * *when* to call `notify(...)` by diffing thread polls.
 *
 * Resilient on platforms without the Notification API (iOS Safari < 16.4,
 * SSR, locked-down WebViews): every accessor short-circuits to a no-op.
 */
export function useBrowserNotifications(): UseBrowserNotificationsResult {
  const [permission, setPermission] = useState<NotificationPermissionState>(() =>
    readNotificationPermission(),
  );

  // Keep `permission` in sync if it changes mid-session — e.g. the user grants
  // access via the URL bar UI without going through our banner. The Permissions
  // API isn't universal, so fall back to a focus listener.
  useEffect(() => {
    if (!isNotificationApiSupported()) return;

    let cancelled = false;
    const sync = () => {
      if (cancelled) return;
      setPermission(readNotificationPermission());
    };

    // Best-effort: Permissions API tells us about external changes.
    let permStatus: PermissionStatus | null = null;
    const nav = window.navigator as Navigator & {
      permissions?: { query: (q: { name: PermissionName }) => Promise<PermissionStatus> };
    };
    if (nav.permissions?.query) {
      nav.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((status) => {
          if (cancelled) return;
          permStatus = status;
          status.onchange = sync;
        })
        .catch(() => {
          // Some browsers reject 'notifications' as a name — ignore.
        });
    }

    // Fallback: re-read on window focus.
    window.addEventListener('focus', sync);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', sync);
      if (permStatus) permStatus.onchange = null;
    };
  }, []);

  const requestPermission = useCallback(async () => {
    const next = await requestNotificationPermission();
    setPermission(next);
    return next;
  }, []);

  const notify = useCallback(
    (args: NotifyArgs): boolean => {
      if (permission !== 'granted') return false;
      const n = spawnNotification(args.title, {
        body: args.body,
        tag: args.threadId,
        onClick: args.onClick,
      });
      return n !== null;
    },
    [permission],
  );

  return {
    permission,
    isSupported: permission !== 'unsupported',
    canRequest: permission === 'default',
    requestPermission,
    notify,
  };
}
